import { useEffect, useMemo, useState } from "react";
import { DefaultTheme } from "styled-components";
import { CustomtOption, SectionLayout } from "@utils/customization";

export const useFilteredOptions = (
  theme: DefaultTheme,
  priceMode?: number,
  sectionLayout?: SectionLayout
) => {
  const [customOptions, setCustomOptions] = useState<CustomtOption[]>([]);

  const screenSizes = useMemo(() => {
    const sizes: string[] = [];
    if (theme.isMobile) sizes.push("s");
    if (theme.isTablet) sizes.push("m");
    if (theme.isDesktop) sizes.push("l");
    return sizes;
  }, [theme]);

  useEffect(() => {
    if (sectionLayout?.options) {
      const subSections = Object.values(sectionLayout.options);
      const filteredOptions = subSections
        .filter((option) => {
          const hasValidPriceMode =
            Array.isArray(option.price_modes) &&
            option.price_modes.includes(priceMode);
          const matchesScreenWidth =
            !option.screen_width ||
            option.screen_width.some((width) => screenSizes.includes(width));
          return hasValidPriceMode && matchesScreenWidth;
        })
        .sort((a, b) => {
          if (a.isSingleRow) {
            return (a.webOrder || 0) - (b.webOrder || 0);
          }
          return (a.order || 0) - (b.order || 0);
        });

      setCustomOptions(filteredOptions);
    }
  }, [sectionLayout, priceMode, screenSizes]);

  return customOptions;
};

export const useFilterSections = (
  theme: DefaultTheme,
  priceMode?: number,
  sectionLayout?: SectionLayout
) => {
  const [subSections, setSubSections] = useState<{
    [key: string]: CustomtOption[];
  }>({});

  const screenSizes = useMemo(() => {
    const sizes: string[] = [];
    if (theme.isMobile) sizes.push("s");
    if (theme.isTablet) sizes.push("m");
    if (theme.isDesktop) sizes.push("l");
    return sizes;
  }, [theme]);

  useEffect(() => {
    if (sectionLayout && sectionLayout.options) {
      const newSubSections: { [key: string]: CustomtOption[] } = {};

      if (sectionLayout.sub_section) {
        Object.entries(sectionLayout.sub_section).forEach(([key, value]) => {
          if (value.options) {
            const subSectionOptions = Object.values(value.options);
            const filteredSubSectionOptions = subSectionOptions
              .filter((option: any) => {
                const hasPriceModes =
                  Array.isArray(option.price_modes) &&
                  option.price_modes.includes(priceMode);
                const matchesScreenWidth =
                  !option?.screen_width ||
                  option?.screen_width.some((width) =>
                    screenSizes.includes(width)
                  );

                return hasPriceModes && matchesScreenWidth;
              })
              .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
            newSubSections[key] = filteredSubSectionOptions;
          }
        });
      }

      setSubSections(newSubSections);
    }
  }, [sectionLayout, priceMode, screenSizes]); // Use `screenSizes` here as a dependency

  return subSections;
};

export const useFilteredSection = (
  theme: DefaultTheme,
  priceMode?: number,
  sectionLayout?: SectionLayout
) => {
  const [customOptions, setCustomOptions] = useState<CustomtOption[]>([]);

  const screenSizes = useMemo(() => {
    const sizes: string[] = [];
    if (theme.isMobile) sizes.push("s");
    if (theme.isTablet) sizes.push("m");
    if (theme.isLargeScreen) sizes.push("l");
    return sizes;
  }, [theme]);

  useEffect(() => {
    if (sectionLayout?.sub_section) {
      const subSections = Object.values(sectionLayout?.sub_section);
      const filteredOptions = subSections
        .filter((subSection) => {
          const hasValidPriceMode =
            Array.isArray(subSection.price_modes) &&
            subSection.price_modes.includes(priceMode);
          const matchesScreenWidth =
            !subSection.screen_width ||
            subSection.screen_width.some((width) =>
              screenSizes.includes(width)
            );
          return hasValidPriceMode && matchesScreenWidth;
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      setCustomOptions(filteredOptions);
    }
  }, [sectionLayout, priceMode, screenSizes]);

  return customOptions;
};
